import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Btn } from '../components';
import { palette as T, alpha, type } from '../design';

function shannonEntropy(str) {
  const len = str.length;
  if (len === 0) return 0;
  const freq = {};
  for (const ch of str) {
    freq[ch] = (freq[ch] || 0) + 1;
  }
  let entropy = 0;
  for (const count of Object.values(freq)) {
    const p = count / len;
    entropy -= p * Math.log2(p);
  }
  return entropy * len;
}

function getPasswordStrength(password) {
  if (!password || password.length < 8) return { score: 0, label: 'Too short', color: T.errorRed };
  const entropy = shannonEntropy(password);
  if (entropy < 30) return { score: 1, label: 'Weak', color: T.errorRed };
  if (entropy < 45) return { score: 2, label: 'Fair', color: T.caramelGold };
  if (entropy < 60) return { score: 3, label: 'Good', color: T.sageGreen };
  return { score: 4, label: 'Strong', color: T.sageDark };
}

// The emailed link is hash-routed (#/set-password?token=...), so the token lives in the
// hash, not window.location.search. Copy here stays neutral between the two flows that land
// on this page: an admin invite and a password reset (TTT-57).
function getTokenFromHash() {
  const hash = window.location.hash;
  const q = hash.indexOf('?');
  if (q === -1) return null;
  return new URLSearchParams(hash.slice(q + 1)).get('token');
}

export function SetPasswordPage({ setPage }) {
  const { setPassword } = useAuth();
  const [password, setPasswordVal] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const token = useMemo(getTokenFromHash, []);


  const strength = useMemo(() => getPasswordStrength(password), [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('This link is missing its token. Request a new one from the login page.');
      return;
    }
    if (password.length < 8) {
      setError('Your password must be at least 8 characters.');
      return;
    }
    if (strength.score < 2) {
      setError('Password is too weak. Try a longer or more varied passphrase.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Those passwords do not match. Please retype them.');
      return;
    }
    setLoading(true);
    try {
      await setPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err.message || 'Could not set your password. This link may have expired or already been used — request a new one from the login page.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px 40px', background: T.ivory }}>
        <div style={{ width: '100%', maxWidth: 420, background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: '32px 28px', textAlign: 'center' }}>
          <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 24, color: T.sageGreen, margin: '0 0 12px' }}>Password saved</h1>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, color: T.walnut, marginBottom: 24 }}>
            Your password is set and your email is verified. You may now log in.
          </p>
          <button
            onClick={() => setPage('login')}
            style={{ background: T.terracotta, color: T.white, border: 'none', borderRadius: 12, padding: '12px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, cursor: 'pointer' }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px 40px', background: T.ivory }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 32, color: T.espresso, margin: '0 0 8px' }}>Set your password</h1>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, color: T.walnut }}>Choose a new password for your account</p>
        </div>

        <form onSubmit={handleSubmit} style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: '32px 28px' }}>
          {error && (
            <div style={{ background: alpha('errorRed', 'tint'), border: '1px solid ' + alpha('errorRed', 'selection'), borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: 0 }}>{error}</p>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, marginBottom: 6 }}>New password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPasswordVal(e.target.value)}
              placeholder="Min. 8 characters"
              required
              minLength={8}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 12, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none', boxSizing: 'border-box' }}
            />
            {password.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{ flex: 1, height: 4, borderRadius: 2, background: T.warmGrey, overflow: 'hidden' }}>
                    <div style={{ width: `${(strength.score / 4) * 100}%`, height: '100%', background: strength.color, borderRadius: 2, transition: 'all 0.2s' }} />
                  </div>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, fontWeight: 600, color: strength.color }}>{strength.label}</span>
                </div>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: 0 }}>
                  Use 8+ characters. Avoid common words.
                </p>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, marginBottom: 6 }}>Confirm password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              required
              minLength={8}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 12, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          <Btn style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
            {loading ? 'Saving…' : 'Save password →'}
          </Btn>
        </form>

        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <button onClick={() => setPage('login')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>Back to login</button>
        </div>
      </div>
    </div>
  );
}
